import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { capitalize, isEmpty } from 'lodash';
import { HiOutlineSearch } from 'react-icons/hi';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { FaRegFileAlt } from 'react-icons/fa';
import { AiFillCloseCircle } from 'react-icons/ai';
import dayjs from 'dayjs';

import { removeEmpty, usePersistState } from 'helper';
import Table, { tableEditDelete } from 'partial/components/Table';
import Button from 'partial/components/Button';
import Pagination from 'partial/components/Pagination';
import FormInlineInput from 'partial/form/FormInlineInput';
import FormInlineSelect from 'partial/form/FormInlineSelect';
import FormInlineDateRange from 'partial/form/FormInlineDateRange';
import { useGetProfile } from 'core-modules/profile/hooks';
import { useDownloadFile } from 'partial/hooks';

import { useOrgSettlementList } from '../hooks';

const STATUS_CLASS = {
  success: 'text-secondary-800',
  pending: 'text-yellow-500',
};

function OrgSettlement() {
  const match = useRouteMatch();
  const history = useHistory();
  const [filter, setFilter] = usePersistState('org-settlement-filter', {});
  const [profile] = useGetProfile();
  const downloadUrl = useMemo(
    () =>
      `/wallet/v1/biller_mgmt/organizations/${
        profile?.organization_id
      }/settlement/export?sort=desc${filter?.q ? `&q=${filter?.q}` : ''}${
        filter?.status ? `&status=${filter?.status}` : ''
      }${
        filter?.date_from && filter?.date_to
          ? `&date_from=${filter?.date_from}&date_to=${filter?.date_to}`
          : ''
      }`,
    [filter, profile]
  );
  const [exportFile, isExporting] = useDownloadFile({
    downloadUrl,
    fileName: `ORG_SETTLEMENT_LIST_${
      filter?.date_from && filter?.date_to
        ? `${filter?.date_from}-${filter?.date_to}`
        : dayjs().format('DD/MM/YYYY')
    }`,
  });
  const [isLoading, data, pagination, fetchData] = useOrgSettlementList();

  const tableFormat = useMemo(() => {
    const cols = [
      {
        label: 'Date Request',
        key: 'date_request',
        id: 'date_request',
      },
      {
        label: 'Request Ref No.',
        key: 'request_ref_no',
        id: 'request_ref_no',
      },
      {
        label: 'Biller Name',
        key: (row) => <span>{row?.merchant_details?.name}</span>,
        id: 'biller_name',
      },
      {
        label: 'Amount Request',
        key: 'amount_request',
        id: 'amount_request',
      },
      {
        label: 'Settlement Status',
        key: (row) => (
          <span className={`${STATUS_CLASS[row?.settlement_status]}`}>
            {capitalize(row?.settlement_status)}
          </span>
        ),
        id: 'settlement_status',
      },
      tableEditDelete((row) => {
        history.push(`${match.url}/${row?.merchant_id}/view/${row?.id}`, row);
      }),
    ];
    return cols;
  }, [history, match]);

  useEffect(() => {
    fetchData(filter);
  }, [fetchData, filter]);
  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow m-5">
      <div className="p-5">
        <Filter onSubmit={setFilter} />
      </div>
      <div className="flex flex-1 border-b">
        <Table
          data={data?.list || []}
          format={tableFormat}
          isLoading={isLoading}
        />
      </div>
      <div className="grid grid-cols-5 place-items-center p-5">
        <div className="w-full pr-4 border-r">
          <Pagination
            small
            showPagesLabel
            onChange={setFilter}
            data={pagination}
          />
        </div>
        <div className="flex w-full border-r">
          <div className="mx-auto">
            <p className="text-base">Amount Available for Cash Out</p>
            <p className="text-primary-800 text-xl font-semibold">
              B${data?.accumulated_amount || '0.00'}
            </p>
          </div>
        </div>
        <div className="flex w-full border-r">
          <div className="mx-auto">
            <p className="text-base">Amount Pending Cash Out</p>
            <p className="text-primary-800 text-xl font-semibold">
              B${data?.pending_amount || '0.00'}
            </p>
          </div>
        </div>
        <div className="flex w-full border-r">
          <div className="mx-auto">
            <p className="text-base">Pending Dispute Amount</p>
            <p className="text-primary-800 text-xl font-semibold">
              B${data?.total_pending_dispute_amount || '0.00'}
            </p>
          </div>
        </div>
        <div className="justify-self-center">
          <Button
            size="sm"
            outlinePrimary
            className="w-32 h-12"
            icon={FaRegFileAlt}
            isLoading={isExporting}
            onClick={exportFile}
          >
            Export
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OrgSettlement;

function Filter({ onSubmit }) {
  const [form, setForm] = useState();
  const handleOnSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const args = {
        q: form?.q || '',
        status: form?.settlement_status || '',
        date_from: form?.date_range?.start || '',
        date_to: form?.date_range?.end || '',
      };
      onSubmit(removeEmpty(args));
    },
    [onSubmit, form]
  );
  return (
    <form
      onSubmit={handleOnSubmit}
      className="flex justify-between items-center"
    >
      <div className="flex items-center space-x-3">
        <FormInlineInput
          name="q"
          onChange={setForm}
          value={form?.q || ''}
          icon={HiOutlineSearch}
          placeholder="Search..."
        />
        <div className="w-72">
          <FormInlineSelect
            name="settlement_status"
            label="Settlement Status"
            onChange={setForm}
            value={form?.settlement_status || ''}
            options={[
              {
                label: 'All',
                value: '',
              },
              {
                label: 'Success',
                value: 'success',
              },
              {
                label: 'Pending',
                value: 'pending',
              },
            ]}
          />
        </div>
        <div className="flex items-center">
          <FormInlineDateRange
            name="date_range"
            onChange={setForm}
            value={form?.date_range || {}}
          />
          {!isEmpty(form?.date_range?.start) &&
            !isEmpty(form?.date_range?.end) && (
              <button
                type="button"
                className="ml-2"
                onClick={() => {
                  setForm((prev) => ({
                    q: prev?.q,
                    settlement_status: prev?.settlement_status,
                    date_range: {
                      start: '',
                      end: '',
                    },
                  }));
                }}
              >
                <AiFillCloseCircle className="w-5 h-5 text-gray-500" />
              </button>
            )}
        </div>
      </div>
      <div className="flex items-center space-x-3">
        <Button type="submit" primary>
          Filter Selection
        </Button>
      </div>
    </form>
  );
}

Filter.propTypes = {
  onSubmit: PropTypes.instanceOf(Function).isRequired,
};
